/**
 * <school-music-embed>
 *
 * Defers loading the Spotify iframe until the block is close to the viewport.
 * The music section sits well below the fold, and a Spotify embed is a heavy
 * third-party frame, so loading it on page load would cost the school page its
 * out-of-the-box Horizon performance for content most visitors never scroll to.
 *
 * The iframe is authored with `data-src`; this promotes it to `src` on first
 * intersection. Browsers without IntersectionObserver load it immediately, and
 * a <noscript> fallback in the block covers JS being off entirely.
 */
class SchoolMusicEmbed extends HTMLElement {
  #observer = null;

  connectedCallback() {
    const iframe = this.querySelector('iframe[data-src]');
    if (!iframe) return;

    if (!('IntersectionObserver' in window)) {
      this.#load(iframe);
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#load(iframe);
          this.#disconnect();
        }
      },
      { rootMargin: '300px 0px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#disconnect();
  }

  #load(iframe) {
    const src = iframe.dataset.src;
    if (!src || iframe.src) return;
    iframe.src = src;
    iframe.removeAttribute('data-src');
    this.setAttribute('loaded', '');
  }

  #disconnect() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('school-music-embed')) {
  customElements.define('school-music-embed', SchoolMusicEmbed);
}
